import { useToast } from "@medimo/ui";
import {
  fetchUserAttributes,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from "aws-amplify/auth";
import { useCallback, useEffect, useState } from "react";

export type SetupStep = "setup" | "verify" | "complete" | null;

export interface TOTPSetupData {
  sharedSecret: string;
  getSetupUri: (appName: string, username: string) => URL;
}

interface UseTOTPSetupProps {
  onSetupComplete?: () => void;
}

export const useTOTPSetup = ({ onSetupComplete }: UseTOTPSetupProps = {}) => {
  const [currentStep, setCurrentStep] = useState<SetupStep>(null);
  const [setupData, setSetupData] = useState<TOTPSetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  );
  const [email, setEmail] = useState("２要素認証ユーザー");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchEmail = async () => {
      try {
        const userAttributes = await fetchUserAttributes();
        setEmail(userAttributes?.email || "２要素認証ユーザー");
      } catch (_error) {
        toast({
          title: "ユーザー情報の取得に失敗",
          description: "ユーザーのメールアドレスを取得できませんでした。",
          variant: "destructive",
        });
      }
    };
    fetchEmail();
  }, [toast]);

  // Open modal when moving to verify
  useEffect(() => {
    if (currentStep === "verify") {
      setIsModalOpen(true);
    }
  }, [currentStep]);

  const initializeTOTPSetup = useCallback(async () => {
    setIsLoading(true);
    setCurrentStep("setup");
    try {
      const totpSetupDetails = await setUpTOTP();
      setSetupData(totpSetupDetails);
    } catch (_error) {
      toast({
        title: "セットアップエラー",
        description:
          "ワンタイムパスワードの初期化に失敗しました。もう一度お試しください。",
        variant: "destructive",
      });
      throw _error;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const handleVerifyCode = useCallback(async () => {
    if (!verificationCode.trim()) {
      setVerificationError("6桁の認証コードを入力してください。");
      return;
    }

    setIsVerifying(true);
    setVerificationError(null);
    try {
      await verifyTOTPSetup({ code: verificationCode });
      await updateMFAPreference({
        totp: "ENABLED",
      });
      setCurrentStep("complete");
    } catch (_error) {
      setVerificationError(
        "認証コードが正しくありません。もう一度お試しください。",
      );
    } finally {
      setIsVerifying(false);
    }
  }, [verificationCode]);

  const handleCopySecret = useCallback(async () => {
    if (setupData?.sharedSecret) {
      try {
        await navigator.clipboard.writeText(setupData.sharedSecret);
        setSecretCopied(true);
        toast({
          title: "コピーしました",
          description: "シークレットキーをクリップボードにコピーしました。",
        });
        setTimeout(() => setSecretCopied(false), 2000);
      } catch (_error) {
        toast({
          title: "コピーに失敗しました",
          description: "シークレットキーのコピーに失敗しました。",
          variant: "destructive",
        });
      }
    }
  }, [setupData?.sharedSecret, toast]);

  const resetState = useCallback(() => {
    setCurrentStep(null);
    setSetupData(null);
    setVerificationCode("");
    setSecretCopied(false);
    setVerificationError(null);
    setIsLoading(false);
    setIsVerifying(false);
    setIsInitialized(false);
    setIsModalOpen(false);
  }, []);

  const handleStepChange = useCallback((step: SetupStep) => {
    setCurrentStep(step);
    if (step === "verify") {
      setVerificationError(null);
    }
  }, []);

  const handleVerificationCodeChange = useCallback(
    (code: string) => {
      setVerificationCode(code.replace(/\D/g, "").slice(0, 6));
      if (verificationError) {
        setVerificationError(null);
      }
    },
    [verificationError],
  );

  const handleInitializeSetup = useCallback(async () => {
    if (!(isInitialized || setupData)) {
      setIsInitialized(true);
      try {
        await initializeTOTPSetup();
      } catch (error) {
        setIsInitialized(false);
        throw error;
      }
    }
  }, [isInitialized, setupData, initializeTOTPSetup]);

  const handleNextToVerify = useCallback(() => {
    handleStepChange("verify");
  }, [handleStepChange]);

  const handleCompleteSetup = useCallback(() => {
    resetState();
    onSetupComplete?.();
  }, [resetState, onSetupComplete]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    // Only reset state after modal animation completes
    if (!isModalOpen) {
      if (currentStep === "verify") {
        handleStepChange("setup");
      } else if (currentStep === "complete") {
        handleCompleteSetup();
      }
    }
  }, [isModalOpen, currentStep, handleStepChange, handleCompleteSetup]);

  return {
    // ステート
    currentStep,
    setupData,
    verificationCode,
    isLoading,
    isVerifying,
    secretCopied,
    verificationError,
    email,
    isInitialized,
    isModalOpen,

    // アクション
    initializeTOTPSetup,
    handleVerifyCode,
    handleCopySecret,
    handleStepChange,
    handleVerificationCodeChange,
    resetState,
    handleInitializeSetup,
    handleModalClose,
    handleNextToVerify,
    handleCompleteSetup,
    handleAnimationEnd,
  };
};
